/* Domain dependencies */
import { userModel, accessOptionsByUser } from '@/domain/user/user.model';
import { platform } from '@/domain/global.enum';

/* Application dependencies */
import { authenticateUserExecute } from '@/application/user/authenticate-user.use-case';
import {
  verifySessionExecute,
  dateUserExecute,
} from '@/application/user/verify-session.use-case';
import { logoutUserExecute } from '@/application/user/logout-user.use-case';

/* Infrastructure dependencies */
import { ALL_ACCESS_OPTIONS } from '@/infrastructure/utils/constants';
import { selectMessage } from '@/infrastructure/utils/modals';
import {
  API_LOGIN_MESSAGES,
  API_LOGOUT_MESSAGES,
  API_STATUS_SESSION_MESSAGES,
} from '@/infrastructure/apis/messages/auth.message';
import {
  apiLogin,
  apiStatusSession,
  apiLogout,
} from '@/infrastructure/apis/auth';
import { apiGetDataUser } from '@/infrastructure/apis/attention-queues';
import { apiValidDocument } from '@/infrastructure/apis/client';
import md5 from 'md5';
import { PATHS } from '@/infrastructure/utils/route-constants';
import router from '../../router';

const namespaced = true;

const state = () => ({
  user: userModel,
  loggedIn: false,
});

const getters = {
  isAuthenticated(state) {
    return state.loggedIn;
  },
  user: (state) => state.user,
  assignmentDetail: (state) => state.user.assignmentDetail || {},
  accessOptionsByProfile: (state) =>
    accessOptionsByUser(state.user.accessOptions, ALL_ACCESS_OPTIONS),
};

const mutations = {
  SET_LOGGED_IN(state, value) {
    state.loggedIn = value;
  },
  SET_USER_DATA(state, payload) {
    state.user = { ...state.user, ...payload };
  },
  SET_HEADQUARTERS_DATA(state, payload) {
    state.user.headquartersCode = payload.headquartersCode;
    state.user.headquartersShort = payload.headquartersShort;
    state.user.assignmentDetail.headquartersCode = payload.headquartersCode;
  },
  CLEAR_DATA(state) {
    state.user = userModel;
    state.loggedIn = false;
  },
};

const actions = {
  async loginAction({ rootState, commit, dispatch }, payload) {
    try {
      const payloadAuthenticateUser = {
        platform: platform.GESCOL,
        request: {
          user: payload.username,
          password: md5(payload.password),
          loginDetails: {
            ip: rootState.ipDevice,
            phyadd: '',
            browser: window.navigator.userAgent,
            channel: 1,
          },
        },
      };
      const responseAuthenticateUser = await authenticateUserExecute(
        payloadAuthenticateUser,
        apiLogin
      );
      commit('SET_USER_DATA', {
        userCode: responseAuthenticateUser.dataUser.userCode,
        userName: responseAuthenticateUser.dataUser.user,
        entityCode: responseAuthenticateUser.dataUser.entityCode,
        platform: responseAuthenticateUser.dataUser.platform,
        accessOptions: responseAuthenticateUser.dataUser.accessOptions,
      });
      commit('SET_LOGGED_IN', true);
      router.push(PATHS.dashboard.pathBase);
      const message = selectMessage({
        responseCode: responseAuthenticateUser.responseCode,
        messageObject: API_LOGIN_MESSAGES,
      });
      dispatch('alerts/addAlert', message, { root: true });
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_LOGIN_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },

  async validationAction({ commit, dispatch }, payload) {
    try {
      const responseAuthenticateUser = await authenticateUserExecute(
        payload,
        apiValidDocument
      );
      commit('SET_USER_DATA', responseAuthenticateUser);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_LOGIN_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async statusSessionAction({ commit, dispatch }) {
    try {
      const responseVerifySession = await verifySessionExecute(
        apiStatusSession
      );
      commit('SET_USER_DATA', {
        userCode: responseVerifySession.userCode,
        userName: responseVerifySession.user,
        entityCode: responseVerifySession.entityCode,
        platform: responseVerifySession.platform,
        accessOptions: responseVerifySession.accessOptions,
      });
      commit('SET_LOGGED_IN', true);
    } catch (error) {
      commit('SET_LOGGED_IN', false);
      commit('CLEAR_DATA');
      localStorage.removeItem('token');
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_STATUS_SESSION_MESSAGES,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },

  async userDataAction({ state, commit, dispatch }) {
    try {
      const userCode = localStorage.getItem('userCode');
      const responseGetDateUser = await dateUserExecute(
        userCode,
        apiGetDataUser
      );
      commit('SET_USER_DATA', {
        headquartersCode: responseGetDateUser[0].headquartersCode,
        headquartersShort: responseGetDateUser[0].headquartersShort,
        headquartersName: responseGetDateUser[0].headquartersName,
        attentionType: responseGetDateUser[0].attentionType,
        usuariName: responseGetDateUser[0].usuariName,
        assignmentDetail: {
          ...responseGetDateUser[0].assignmentDetail,
          headquartersCode: responseGetDateUser[0].headquartersCode,
          attentionType: responseGetDateUser[0].attentionType,
          userCode: state.user.userCode,
        },
      });
    } catch (error) {
      commit('CLEAR_DATA');
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_STATUS_SESSION_MESSAGES,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async logoutAction({ commit, dispatch }) {
    try {
      const responseLogoutUser = await logoutUserExecute(
        platform.GESCOL,
        apiLogout
      );
      const message = selectMessage({
        responseCode: responseLogoutUser.responseCode,
        messageObject: API_LOGOUT_MESSAGES,
      });
      dispatch('alerts/addAlert', message, { root: true });
      commit('CLEAR_DATA');
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_LOGOUT_MESSAGES,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
};

export default {
  namespaced,
  state,
  getters,
  mutations,
  actions,
};
